import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Button, InputField } from '@rocketlane/ui';
import {
  GroupByOption,
  ParticipantGroupOption,
  GenerateAsOption,
} from 'api/custom-section/types';
import { PageType } from 'api/page-template/types';
import { useEffect, useRef, useState } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { SharedConfigurationBar } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-bar';
import { SectionHeading } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-heading';
import {
  SectionCard,
  TextAreaContainer,
  SectionContent,
  SectionDivider,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { ConfigurationBar } from '../../../shared/section-styles';

export const ActionPromptInput = ({
  prompt,
  onUndo,
  setPrompt,
  onGenerate,
  isGenerating,
  onCancel,
  allowResolve,
  getNodeAsPayload,
  pageType,
  pageTriggerListFilter,
}) => {
  const [form] = Form.useForm<{ title: string; aiContext: string }>();
  const [showNext, setShowNext] = useState(!prompt.title?.trim());
  const titleRef = useRef<HTMLInputElement>(null);
  const aiContextRef = useRef<HTMLTextAreaElement>(null);

  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.action_section.title_placeholder',
  );
  const contextPlaceholder = useTranslation(
    'rich_text_editor.sections.section.context_placeholder',
  );

  const isDisabled = isGenerating;

  useEffect(() => {
    form.setFieldsValue({
      title: prompt.title,
      aiContext: prompt.aiContext,
    });
  }, [form, prompt]);

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.select();
    });
  }, []);

  const generateAsOptionsFormatted = useMemo(() => {
    const GenerateAsOptionMapped = [GenerateAsOption.Checklist];

    return Object.values(GenerateAsOption).filter((option) =>
      GenerateAsOptionMapped.includes(option),
    );
  }, []);

  const canShowPreviewButton =
    pageType === PageType.Meeting || pageType === PageType.Email;

  return (
    <SectionCard>
      <SectionHeading
        title="editor.sections.action_section.title"
        onClose={onUndo}
        showCloseButton={!isDisabled}
      />
      <SectionContent>
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label={
              <FormattedMessage id="rich_text_editor.sections.question_section.title" />
            }
          >
            <InputField
              type="text"
              ref={titleRef}
              placeholder={titlePlaceholder}
              onChange={(e) => setPrompt({ ...prompt, title: e.target.value })}
            />
          </Form.Item>
          {!showNext && (
            <Form.Item
              name="aiContext"
              label={
                <FormattedMessage id="rich_text_editor.sections.question_section.add_ai_context" />
              }
            >
              <TextAreaContainer
                id="aiContext"
                ref={aiContextRef}
                value={prompt.aiContext || ''}
                onChange={(e) =>
                  setPrompt({ ...prompt, aiContext: e.target.value })
                }
                placeholder={contextPlaceholder}
              />
            </Form.Item>
          )}
        </Form>
        {showNext ? (
          <ConfigurationBar>
            <Button
              variant="primary"
              disabled={!prompt.title?.trim()}
              onClick={() => {
                setShowNext(false);
              }}
            >
              <FormattedMessage id="next" />
            </Button>
          </ConfigurationBar>
        ) : (
          <>
            <SectionDivider />
            <SharedConfigurationBar
              prompt={prompt}
              setPrompt={setPrompt}
              isActionLoading={isGenerating}
              onCancel={onCancel}
              onSubmit={onGenerate}
              validation={{
                isDisabled,
              }}
              allowResolve={allowResolve}
              getNodeAsPayload={getNodeAsPayload}
              defaultValues={{
                groupBy: GroupByOption.Participants,
                generateAs: GenerateAsOption.Checklist,
                participants: ParticipantGroupOption.All,
              }}
              options={{
                groupBy: Object.values(GroupByOption),
                generateAs: Object.values(generateAsOptionsFormatted),
                participants: Object.values(ParticipantGroupOption),
              }}
              actionBarConfig={{
                canShowPreviewButton,
                canWrapTrashButton: true,
              }}
              pageType={pageType}
              pageTriggerListFilter={pageTriggerListFilter}
            />
          </>
        )}
      </SectionContent>
    </SectionCard>
  );
};
